import React, { useMemo } from 'react';
import { Values } from '@shared/utils/objectEnums';
import {
	AccountingSystemProviders,
	Departments,
	FixtureCounterpartyTypes,
} from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { GetConnectionAndMappingResponse } from '@api/features/accounting-system/getConnectionAndMapping';
import { FixtureCounterpartyProps } from '@api/models/fixture-counterparty';
import EditableTable from '@client/components/EditableTable/EditableTable';
import Card from '@client/components/Card/Card';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	createMappingEntry,
	getAccountingCustomers,
	getAccountingSuppliers,
	getCounterparties,
	getUserData,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';

const D365_PROVIDERS = [AccountingSystemProviders['365bc'], AccountingSystemProviders['365fo']];

type CounterpartyRow = FixtureCounterpartyProps & {
	customerCode: string | undefined;
	vendorCode: string | undefined;
};

const CounterpartyMappingTab = ({
	connection,
	refreshConnection,
}: {
	connection: GetConnectionAndMappingResponse | undefined;
	refreshConnection: () => void;
}) => {
	const [userData] = useFetchedState(getUserData);

	const [counterparties, refreshCounterparties] = useFetchedState(getCounterparties);

	const isD365 = connection?.provider != null && D365_PROVIDERS.includes(connection.provider as any);

	const [customers] = useFetchedState(async () => (
		connection == null ? [] : getAccountingCustomers(connection.id)
	), [connection]);

	const [vendors] = useFetchedState(async () => (
		connection == null || !isD365 ? [] : getAccountingSuppliers(connection.id)
	), [connection, isD365]);

	const counterpartyList = useMemo((): CounterpartyRow[] => {
		if (counterparties == null || connection == null) {
			return [];
		}

		return counterparties.map((cp) => {
			const customerEntry = connection.AccountingMappingEntries.find((m) => (
				m.type === 'fixtureCounterparty' && m.cvIdentifier === String(cp.id)
			));
			const vendorEntry = connection.AccountingMappingEntries.find((m) => (
				m.type === 'fixtureCounterpartyVendor' && m.cvIdentifier === String(cp.id)
			));

			return {
				...cp,
				customerCode: customerEntry?.code ?? undefined,
				vendorCode: vendorEntry?.code ?? undefined,
			};
		});
	}, [counterparties, connection]);

	const onSave = async (
		counterpartyId: number | undefined,
		update: Partial<GetCounterpartiesResponse[number] & { customerCode: string; vendorCode: string }>,
	) => {
		if (counterpartyId == null || connection == null) {
			refreshCounterparties();

			return;
		}

		const currentRow = counterpartyList.find((cp) => cp.id === counterpartyId);

		if (currentRow == null) {
			refreshCounterparties();

			return;
		}

		let didSave = false;

		if ('customerCode' in update && update.customerCode !== currentRow.customerCode) {
			try {
				await createMappingEntry('fixtureCounterparty', counterpartyId, update.customerCode, connection.id);
				didSave = true;
			} catch (e) {
				showErrorNotification('Could not save customer code mapping', e as Error);
			}
		}

		if (isD365 && 'vendorCode' in update && update.vendorCode !== currentRow.vendorCode) {
			try {
				await createMappingEntry('fixtureCounterpartyVendor', counterpartyId, update.vendorCode, connection.id);
				didSave = true;
			} catch (e) {
				showErrorNotification('Could not save supplier code mapping', e as Error);
			}
		}

		if (didSave) {
			await refreshConnection();
			showSuccessNotification('Successfully mapped entry');
		}

		refreshCounterparties();
	};

	return (
		<Card slim>
			<EditableTable<CounterpartyRow, 'id'>
				actionsTitle=""
				enableEdit={userData?.departments.includes(Departments.FINANCE.key)}
				iconButtons
				pagination={false}
				keyDataIndex="id"
				onSave={onSave}
				dataSource={counterpartyList}
				columns={[
					{
						dataIndex: 'id',
						title: 'ID',
					},
					{
						dataIndex: 'name',
						title: 'Name',
					},
					{
						dataIndex: 'type',
						title: 'Type',
						render: (v: Values<typeof FixtureCounterpartyTypes>) => capitalize(v),
					},
					{
						dataIndex: 'customerCode',
						title: isD365 ? 'Customer code' : 'Accounting System Entry',
						editingProps: {
							type: 'select',
							options: (customers ?? []).map((c) => ({
								label: c.name,
								value: c.id,
							})),
							inputProps: {
								placeholder: 'Select customer',
								showSearch: true,
								allowClear: true,
							},
						},
						editable: true,
						render: (v: string | null) => (
							v == null ? (<i>Empty</i>) : customers?.find((c) => c.id === v)?.name
						),
					},
					...(isD365 ? [{
						dataIndex: 'vendorCode' as const,
						title: 'Supplier code',
						editingProps: {
							type: 'select' as const,
							options: (vendors ?? []).map((v) => ({
								label: v.name,
								value: v.id,
							})),
							inputProps: {
								placeholder: 'Select supplier',
								showSearch: true,
								allowClear: true,
							},
						},
						editable: true,
						render: (v: string | null) => (
							v == null ? (<i>Empty</i>) : vendors?.find((s) => s.id === v)?.name
						),
					}] : []),
				]}
			/>
		</Card>
	);
};

export default CounterpartyMappingTab;
